import type { FC } from 'react';
import { Box, type Theme, styled, type ButtonProps } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';

import { LoadingButton } from '@mui/lab';
import Typography from '../../atoms/Typography';

export const makeDataTestId = (dataTestId = 'upgradeButton') => {
  return {
    prefix: dataTestId,
    icon: `${dataTestId}.icon`,
    iconActive: `${dataTestId}.iconActive`,
  };
};

const StyledLongButton = styled(LoadingButton, {
  shouldForwardProp: (propName) => propName !== 'iconToggle' && propName !== 'active',
})<{
  active?: boolean;
  iconToggle?: boolean;
}>(
  ({ iconToggle, active, theme }: { active?: boolean; iconToggle: boolean; theme: Theme }) => `
  padding: 0.45rem 0.65rem;
  border-radius: 1.5rem;
  position: relative;
  padding-inline-start: ${iconToggle ? '2.25rem' : '1rem'};
  padding-inline-end: 1rem;
  background: ${active ? theme.palette.primary.main : 'transparent'};
  color: ${active ? theme.palette.primary.contrastText : theme.palette.primary.main};
  display: flex;

  word-break: break-word;

  transition: all linear 0.2s;

  &:hover:not(:disabled) {
    border-color: ${theme.palette.primary.dark} !important;
    background: ${theme.palette.primary.dark} !important;
    color: ${theme.palette.primary.contrastText};
  }
`,
);

const StyledIcon = styled(Box)(
  ({ theme }) => `
  position: absolute;
  top: 0;
  ${theme.direction === 'rtl' ? 'right: 0.5rem' : 'left: 0.5rem'};
  bottom: 0;
  display: flex;
  align-items: center;
`,
);

export type UpgradeButtonProps = {
  label: string;
  labelActive?: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: (() => Promise<void>) | (() => void);
  dataTestId?: string;
  iconToggle?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  endIcon?: React.ReactNode;
} & ButtonProps;

const UpgradeButton: FC<UpgradeButtonProps> = ({
  dataTestId,
  active,
  labelActive,
  iconToggle,
  fullWidth,
  endIcon,
  ...props
}) => (
  <StyledLongButton
    variant="outlined"
    {...props}
    active={active}
    data-testid={dataTestId}
    iconToggle={iconToggle}
    fullWidth={fullWidth}
    endIcon={endIcon}
  >
    <ButtonContent
      {...props}
      active={active}
      iconToggle={iconToggle}
      dataTestId={dataTestId}
      labelActive={labelActive}
    />
  </StyledLongButton>
);

export const ButtonContent: FC<
  Pick<UpgradeButtonProps, 'active' | 'label' | 'labelActive' | 'iconToggle' | 'dataTestId'>
> = ({ dataTestId, label, labelActive, iconToggle, active }) => {
  const allDataTestId = makeDataTestId(dataTestId);

  return (
    <>
      {iconToggle && (
        <StyledIcon>
          {active ? (
            <CheckIcon data-testid={allDataTestId.iconActive} fontSize="medium" />
          ) : (
            <AddIcon data-testid={allDataTestId.icon} fontSize="medium" />
          )}
        </StyledIcon>
      )}
      <Typography variant="body2-bold">{active ? (labelActive ?? label) : label}</Typography>
    </>
  );
};

export default UpgradeButton;
